import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import appConfigService, { type WelcomeVideoConfig } from '../services/appConfigService';

interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'certificates-1',
    category: 'Certificates',
    question: 'Where can I find completion certificates for courses I finished?',
    answer:
      'Go to Reporting to view and download your certificates. Webinar completions will link directly to the certificate. On-Demand completetions will link to the on demand course page from there, click "Download Certificate" in the top right corner to get your certificate. If a course was completed recently, it may take a short time to appear while the completion status syncs.',
  },
  {
    id: 'learning-1',
    category: 'Learning',
    question: 'How do I continue a course from where I left off?',
    answer:
      'Open Learning and select Continue on the course card in the My Learning section. Your progress is saved automatically, so you can resume from the last completed section.',
  },
  {
    id: 'account-1',
    category: 'Account',
    question: 'How do I update my profile details or email preferences?',
    answer:
      'Open Profile and use Edit Profile for personal details. For account security, email settings, and authentication preferences, use Manage Account Settings.',
  },
  {
    id: 'billing-1',
    category: 'Billing',
    question: 'How can I change or manage my individual subscription?',
    answer:
      'Go to Billing from your app navigation and review your current plan. You add modules and review your subscription status from there. To request a cancellation or refund, please contact support with details about your subscription and request.',
  },
  {
    id: 'billing-2',
    category: 'Billing',
    question: 'What happens to my individual subscription if I join a company (tenant)?',
    answer:
      'When you join a company that manages subscriptions for its team, your individual subscription is automatically cancelled and you receive a pro-rated refund for the unused portion of your billing period. Your access will continue uninterrupted under the company\'s subscription. No action is needed; the transition is handled automatically. If you later leave the company, you can resubscribe to an individual plan from the Billing page.',
  },
  {
    id: 'compliance-1',
    category: 'Compliance Tracking',
    question: 'How does CPE compliance tracking work?',
    answer:
      'Add your state licenses on the License Management page. For each license, we suggest the CPE requirements and reporting period based on your state\'s published rules. Review them, adjust if needed, and confirm. Your progress against confirmed requirements appears on the Reporting page, and will be updated as you earn credits.',
  },
  {
    id: 'compliance-2',
    category: 'Compliance Tracking',
    question: 'Where do the suggested requirements come from?',
    answer:
      'Suggested values are loaded from state board rules that we maintain and periodically review. They are provided for convenience and are not a determination of licensure status. Your State Board of Accountancy is the final authority on your CPE requirements, so always verify with your board before relying on them.',
  },
  {
    id: 'compliance-3',
    category: 'Compliance Tracking',
    question: 'What does the "At Risk" indicator mean?',
    answer:
      'At Risk is an informational estimate that you may be behind pace for the current reporting period: for example, if your earned hours are noticeably below where they would need to be at this point in the period, or a requirement you track manually is still unchecked late in the period. It is a nudge to review your progress, not an official compliance determination.',
  },
  {
    id: 'compliance-4',
    category: 'Compliance Tracking',
    question: 'What does "State data updated" mean on my license?',
    answer:
      'It means the state rule data we maintain for that license\'s state has changed since you confirmed your requirements. Open the license on the License Management page, review the current values, and re-confirm. If you had customized your requirements, your edits are kept; the flag just asks you to take another look.',
  },
  {
    id: 'compliance-5',
    category: 'Compliance Tracking',
    question: 'Do credits earned outside CPAx Academy count toward my requirements?',
    answer:
      'Yes. Use "Add external credit" on the Reporting page to record credits from other providers. External credits count toward your tracked requirements alongside credits earned from CPAx Academy courses.',
  },
  {
    id: 'compliance-6',
    category: 'Compliance Tracking',
    question: 'Why are some state rules shown as text instead of being tracked automatically?',
    answer:
      'Some state rules, like delivery-method minimums or rules whose measurement window doesn\'t match your reporting period, can\'t be reliably computed from your credit history. Rather than track them incorrectly, we show them as reference notes on your license\'s requirement card so you can account for them yourself.',
  },
  {
    id: 'compliance-7',
    category: 'Compliance Tracking',
    question: 'How do carry-over hours work?',
    answer:
      'Some state boards let hours you earned beyond last period\'s requirement count toward the current period. When your credit history shows excess prior-period hours, we suggest a carry-over amount (capped at your state\'s verified limit where we have one), but nothing counts until you confirm it. Confirmed carry-over hours add to your total hours only; ethics, per-year minimums, and subject requirements must still be met with hours earned in the current period. Your state board remains the final authority on what carries over.',
  },
  {
    id: 'support-1',
    category: 'Support',
    question: 'What should I do if my completed credits are not showing up?',
    answer:
      'First, verify the course status shows complete. Then refresh the page and check the Reporting page again. If the issue persists, contact support and include the course name and completion date.',
  },
];

// Stable category sequence — also defines the rendering order on the page.
const CATEGORY_ORDER = ['Certificates', 'Learning', 'Account', 'Billing', 'Compliance Tracking', 'Support'];

// URL-hash anchor for a category section, e.g. "Compliance Tracking" →
// #compliance-tracking (used for deep links from other pages).
function categorySlug(category: string): string {
  return category.toLowerCase().replace(/\s+/g, '-');
}

// Item ids belonging to the category a URL hash points at (empty when the
// hash is absent or doesn't match a category).
function itemIdsForHash(hash: string): string[] {
  const slug = hash.replace(/^#/, '');
  if (!slug) return [];
  return FAQ_ITEMS.filter((item) => categorySlug(item.category) === slug).map((i) => i.id);
}

export default function Faq() {
  const [query, setQuery] = useState('');
  // Deep links like /app/faq#compliance-tracking start with that category's
  // questions expanded (the page remounts on navigation from other routes).
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(itemIdsForHash(window.location.hash))
  );
  const [videoConfig, setVideoConfig] = useState<WelcomeVideoConfig | null>(null);
  const location = useLocation();

  useEffect(() => {
    appConfigService.getAppConfig()
      .then((cfg) => setVideoConfig(cfg.welcomeVideo))
      .catch(() => { /* non-critical */ });
  }, []);

  // Scroll the deep-linked category's section header into view.
  useEffect(() => {
    const slug = location.hash.replace(/^#/, '');
    if (!slug || itemIdsForHash(location.hash).length === 0) return;
    const node = document.getElementById(`faq-${slug}`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const filteredFaqs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return FAQ_ITEMS;
    return FAQ_ITEMS.filter((item) => {
      const haystack = `${item.question} ${item.answer} ${item.category}`.toLowerCase();
      return haystack.includes(normalized);
    });
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<string, FaqItem[]>();
    for (const item of filteredFaqs) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return CATEGORY_ORDER
      .map((category) => ({ category, items: map.get(category) ?? [] }))
      .filter((group) => group.items.length > 0);
  }, [filteredFaqs]);

  const toggleItem = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(filteredFaqs.map((i) => i.id)));
  const collapseAll = () => setExpanded(new Set());

  const hasResults = filteredFaqs.length > 0;
  const allExpanded = hasResults && expanded.size >= filteredFaqs.length;

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      <Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <HelpOutlineIcon color="primary" sx={{ fontSize: '0.9em' }} />
          Help & FAQ
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '65ch' }}>
          Answers to common questions for individual learners using CPAx Academy.
        </Typography>
      </Box>

      {videoConfig?.enabled && videoConfig.embedUrl && (
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <PlayCircleOutlineIcon color="primary" />
              Getting Started
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Watch this quick video to learn how to get the most out of your account.
            </Typography>
            <Box
              component="iframe"
              src={videoConfig.embedUrl}
              title="Getting Started with CPAx Academy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              sx={{
                width: '100%',
                maxWidth: 720,
                aspectRatio: '16 / 9',
                border: 0,
                borderRadius: 1,
                display: 'block',
              }}
            />
          </CardContent>
        </Card>
      )}

      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <TextField
              size="small"
              placeholder="Search questions"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="Clear search"
                      onClick={() => setQuery('')}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {hasResults && (
              <Button
                size="small"
                variant="text"
                onClick={allExpanded ? collapseAll : expandAll}
                sx={{
                  flexShrink: 0,
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </Button>
            )}
          </Box>

          {!hasResults ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 0.75 }}>
                No questions match &ldquo;{query}&rdquo;.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try a different keyword like credits, billing, certificates, or profile.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={4}>
              {grouped.map((group) => (
                <Box
                  key={group.category}
                  id={`faq-${categorySlug(group.category)}`}
                  sx={{ scrollMarginTop: 80 }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.10em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 1.25,
                    }}
                  >
                    {group.category}
                  </Typography>
                  <Box>
                    {group.items.map((item, itemIdx) => {
                      const isLast = itemIdx === group.items.length - 1;
                      return (
                        <Accordion
                          key={item.id}
                          expanded={expanded.has(item.id)}
                          onChange={() => toggleItem(item.id)}
                          disableGutters
                          elevation={0}
                          sx={{
                            background: 'transparent',
                            borderBottom: isLast ? 'none' : '1px solid',
                            borderColor: 'divider',
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                            sx={{
                              px: 0,
                              '& .MuiAccordionSummary-content': { my: 1.25 },
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="h3"
                              sx={{ fontWeight: 500 }}
                            >
                              {item.question}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 0, pt: 0, pb: 2.5 }}>
                            <Typography
                              variant="body1"
                              sx={{ color: 'text.secondary', maxWidth: '70ch' }}
                            >
                              {item.answer}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}

          <Divider sx={{ mt: 4, mb: 2.5 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Didn&rsquo;t find what you needed?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/app/refund-policy"
                variant="text"
                size="small"
                startIcon={<ReceiptLongIcon />}
                sx={{ color: 'text.secondary' }}
              >
                Refund Policy
              </Button>
              <Button
                component={RouterLink}
                to="/app/contact"
                variant="outlined"
                size="small"
                startIcon={<ContactSupportIcon />}
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* NASBA National Registry sponsor statement — required disclosure, always visible */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography
            component="h2"
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 1.25,
            }}
          >
            NASBA Sponsor Statement
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '70ch' }}>
            CPA Crossings, LLC is registered with the National Association of State Boards of
            Accountancy (NASBA) as a sponsor of continuing professional education on the National
            Registry of CPE Sponsors. State boards of accountancy have final authority on the
            acceptance of individual courses for CPE credit. Complaints regarding registered
            sponsors may be submitted to the National Registry of CPE Sponsors through its web
            site:{' '}
            <Link
              href="https://www.nasbaregistry.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.nasbaregistry.org
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}
